class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.insertUpsellDismissed = false;
    this.tryDifferentDismissed = false;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.addEventListener('click', (event) => {
      if (!event.target.closest('.klx-insert-select')) {
        this.querySelectorAll('.klx-insert-select__options').forEach((ol) => { ol.hidden = true; });
      }

      const removeBtn = event.target.closest('.cart-remove-button');
      if (removeBtn && removeBtn.dataset.handle) {
        this.addToUpsellBlacklist(removeBtn.dataset.handle);
      }

      if (event.target.closest('.klx-insert-upsell__dismiss')) {
        const upsell = event.target.closest('.klx-insert-upsell');
        if (upsell) upsell.style.display = 'none';
        this.insertUpsellDismissed = true;
        return;
      }

      const selectTrigger = event.target.closest('.klx-insert-select__trigger');
      if (selectTrigger) {
        const selectEl = selectTrigger.closest('.klx-insert-select');
        const optionsList = selectEl && selectEl.querySelector('.klx-insert-select__options');
        if (optionsList) optionsList.hidden = !optionsList.hidden;
        return;
      }

      const selectOption = event.target.closest('.klx-insert-select__option');
      if (selectOption) {
        const selectEl = selectOption.closest('.klx-insert-select');
        const triggerEl = selectEl && selectEl.querySelector('.klx-insert-select__trigger');
        if (selectEl) selectEl.dataset.value = selectOption.dataset.value;
        if (triggerEl) triggerEl.textContent = selectOption.textContent;
        const optionsList = selectEl && selectEl.querySelector('.klx-insert-select__options');
        if (optionsList) optionsList.hidden = true;
        return;
      }

      const insertBtn = event.target.closest('.klx-insert-upsell__add-btn');
      if (insertBtn && !insertBtn.disabled) {
        event.preventDefault();
        this.handleInsertUpsellAdd(insertBtn);
        return;
      }

      if (event.target.closest('.klx-upsell__dismiss')) {
        const upsell = event.target.closest('.klx-upsell');
        if (upsell) upsell.style.display = 'none';
        this.tryDifferentDismissed = true;
        return;
      }
      const btn = event.target.closest('.klx-upsell__add-btn');
      if (!btn || btn.disabled) return;
      event.preventDefault();
      this.handleUpsellAdd(btn);
    });

    this.applyUpsellBlacklist();
    this.applyInsertUpsellVisibility();
    this.applyTryDifferentDismiss();

    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.applyUpsellBlacklist();
      this.applyInsertUpsellVisibility();
      this.applyTryDifferentDismiss();
      this.klxAnimateFreeShippingBar();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.klxAnimateFreeShippingBar();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.applyUpsellBlacklist();
      this.applyInsertUpsellVisibility();
      this.applyTryDifferentDismiss();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  klxAnimateFreeShippingBar() {
    const fill = this.querySelector('.klx-free-shipping__fill');
    if (!fill) return;
    const pct = Math.min(parseFloat(fill.dataset.pct) || 0, 100);
    fill.style.width = '0%';
    void fill.offsetWidth; // force reflow so browser commits width:0 before transitioning
    fill.style.width = pct + '%';
  }

  getUpsellBlacklist() {
    try {
      return JSON.parse(sessionStorage.getItem('klx_upsell_blacklist') || '[]');
    } catch {
      return [];
    }
  }

  addToUpsellBlacklist(handle) {
    if (!handle) return;
    try {
      const list = this.getUpsellBlacklist();
      if (!list.includes(handle)) {
        list.push(handle);
        sessionStorage.setItem('klx_upsell_blacklist', JSON.stringify(list));
      }
    } catch {}
  }

  applyUpsellBlacklist() {
    const list = this.getUpsellBlacklist();
    if (!list.length) return;
    this.querySelectorAll('.klx-upsell__add-btn[data-handle]').forEach((btn) => {
      if (list.includes(btn.dataset.handle)) {
        const item = btn.closest('.klx-upsell__item');
        if (item) item.style.display = 'none';
      }
    });
    const upsell = this.querySelector('.klx-upsell');
    if (!upsell) return;
    const visible = upsell.querySelectorAll('.klx-upsell__item:not([style*="display: none"])');
    if (!visible.length) upsell.style.display = 'none';
  }

  applyInsertUpsellVisibility() {
    const upsell = this.querySelector('.klx-insert-upsell');
    if (!upsell) return;
    const insertHandles = new Set([
      'ring-insert', 'half-ring-insert', 'ark-insert', 'wedge-insert',
      'ridge-insert', 'ridge-slim-insert', 'step-insert',
      'inserts-4-pack', 'inserts-7-pack', 'starter-kit', 'the-signature-set'
    ]);
    const cartHandles = Array.from(this.querySelectorAll('.cart-remove-button[data-handle]'))
      .map((btn) => btn.dataset.handle);
    const hasRelevantItem = cartHandles.some((h) => insertHandles.has(h));
    if (!hasRelevantItem || this.insertUpsellDismissed) {
      upsell.style.display = 'none';
    } else {
      upsell.style.display = '';
    }
  }

  applyTryDifferentDismiss() {
    if (this.tryDifferentDismissed) {
      const upsell = this.querySelector('.klx-upsell');
      if (upsell) upsell.style.display = 'none';
    }
  }

  async handleInsertUpsellAdd(btn) {
    const selectEl = btn.closest('.klx-insert-upsell__row').querySelector('.klx-insert-select');
    if (!selectEl) return;
    const handle = selectEl.dataset.value;
    if (!handle) return;
    btn.disabled = true;
    btn.textContent = '…';
    try {
      const productRes = await fetch('/products/' + handle + '.js');
      if (!productRes.ok) throw new Error();
      const product = await productRes.json();
      const variantId = product.variants[0].id;
      const form = new FormData();
      form.append('id', variantId);
      form.append('quantity', '1');
      form.append('sections', 'cart-drawer,cart-icon-bubble');
      form.append('sections_url', window.location.pathname);
      const cartRes = await fetch('/cart/add.js', { method: 'POST', body: form });
      if (!cartRes.ok) throw new Error();
      const data = await cartRes.json();
      if (data.sections) {
        this.renderContents({ id: variantId, sections: data.sections });
      }
    } catch {
      btn.disabled = false;
      btn.textContent = 'Add';
    }
  }

  async handleUpsellAdd(btn) {
    const handle = btn.dataset.handle;
    if (!handle) return;
    btn.disabled = true;
    btn.textContent = '…';
    try {
      const productRes = await fetch('/products/' + handle + '.js');
      if (!productRes.ok) throw new Error();
      const product = await productRes.json();
      const variantId = product.variants[0].id;

      const form = new FormData();
      form.append('id', variantId);
      form.append('quantity', '1');
      form.append('sections', 'cart-drawer,cart-icon-bubble');
      form.append('sections_url', window.location.pathname);
      const cartRes = await fetch('/cart/add.js', { method: 'POST', body: form });
      if (!cartRes.ok) throw new Error();
      const data = await cartRes.json();
      if (data.sections) {
        this.renderContents({ id: variantId, sections: data.sections });
      }
    } catch {
      btn.disabled = false;
      btn.textContent = 'Add';
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
